import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useWebSocket, useAPI } from "@huub-web/backend";
import { useToast } from "@smartfaster-ui/components";

import type { InteriorDesignerAction, TreeNode } from "../context/types";
import { flattenTree, type BackendTreeNode } from "./usePhotoLoader";

/**
 * Bridges WebSocket connection state to the InteriorDesigner reducer.
 * Shows toast notifications on connect/disconnect and refreshes
 * the tree on reconnect.
 */
export function useConnectivity({
  dispatch,
  photoId,
  activeNodeId,
  navigateToNode,
}: {
  dispatch: (action: InteriorDesignerAction) => void;
  /**
   * Backend photo ID used for tree refresh on reconnect
   */
  photoId: string;
  activeNodeId: string;
  navigateToNode: ({ photoId }: { photoId: string }) => Promise<void>;
}) {
  const { isConnected } = useWebSocket();
  const { $get } = useAPI();
  const $getRef = useRef($get);
  $getRef.current = $get;

  const { t } = useTranslation();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const prevConnectedRef = useRef<boolean | null>(null);
  const activeNodeIdRef = useRef(activeNodeId);
  activeNodeIdRef.current = activeNodeId;
  const navigateRef = useRef(navigateToNode);
  navigateRef.current = navigateToNode;

  useEffect(() => {
    const prev = prevConnectedRef.current;
    prevConnectedRef.current = isConnected;

    if (prev === null) return;

    if (prev && !isConnected) {
      dispatch({ type: "CONNECTIVITY_LOST" });
      toastRef.current.show({
        title: t("interiorDesigner.connectivity.lost"),
        variant: "warning",
      });
      return;
    }

    // Only proceed on reconnect (false → true transition)
    if (prev || !isConnected) return;

    dispatch({ type: "CONNECTIVITY_RESTORED" });
    toastRef.current.show({
      title: t("interiorDesigner.connectivity.restored"),
      variant: "success",
    });

    let cancelled = false;

    $getRef
      .current(`/interior-designer/photo/${photoId}/tree`)
      .then((response: { data: BackendTreeNode | null }) => {
        if (cancelled) return;
        const { data } = response;
        if (!data) {
          console.warn(
            "[useConnectivity] Tree endpoint returned null — skipping refresh",
          );
          return;
        }

        const newTree: TreeNode[] = flattenTree({ node: data });
        dispatch({ type: "TREE_REFRESHED", tree: newTree });

        const currentNodeId = activeNodeIdRef.current;
        const root = newTree[0];
        const nodeStillValid = newTree.some(
          (n) => n.id === currentNodeId && n.deleted_at === null,
        );

        if (!nodeStillValid && root) {
          dispatch({ type: "NODE_NAVIGATED", nodeId: root.id });
          navigateRef.current({ photoId: root.id }).catch(() => {});
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.warn("[useConnectivity] Tree refresh failed:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [isConnected, dispatch, photoId, t]);
}
